import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Avatar,
  TextField, InputAdornment,
} from '@mui/material';
import { People as PeopleIcon, Search as SearchIcon, Nfc as NfcIcon } from '@mui/icons-material';
import api from '../../services/api';

interface Employee {
  id: number; nome: string; email?: string; departamento?: string;
  cargo?: string; reward_amount: number; nfc_uid?: string;
  ativo: number; saldo_bonus: number; saldo: number;
}

const fmt = (v: number) => `${v.toLocaleString('pt-CV')} ECV`;

const CorporatePortalEmployees: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [search, setSearch]       = useState('');

  useEffect(() => {
    api.get('/portal/corporate/employees')
      .then(r => setEmployees(r.data.data || []))
      .catch(() => setError('Erro ao carregar funcionários'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = employees.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return e.nome.toLowerCase().includes(q) ||
      (e.departamento || '').toLowerCase().includes(q) ||
      (e.cargo || '').toLowerCase().includes(q);
  });

  const totalBudget = employees.filter(e => e.ativo).reduce((s, e) => s + Number(e.reward_amount), 0);
  const comBracelet = employees.filter(e => e.nfc_uid).length;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Avatar sx={{ bgcolor: '#6233A0' }}><PeopleIcon /></Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>Funcionários</Typography>
          <Typography variant="body2" color="text.secondary">
            {employees.length} registados · {comBracelet} com bracelet · budget {fmt(totalBudget)}/mês
          </Typography>
        </Box>
      </Box>

      <TextField size="small" placeholder="Pesquisar por nome, departamento..."
        value={search} onChange={e => setSearch(e.target.value)}
        sx={{ mb: 2, width: 300 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><b>Nome</b></TableCell>
                <TableCell><b>Departamento</b></TableCell>
                <TableCell><b>Cargo</b></TableCell>
                <TableCell align="center"><b>Bracelet</b></TableCell>
                <TableCell align="right"><b>Recompensa/mês</b></TableCell>
                <TableCell align="right"><b>Saldo Disponível</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum resultado
                </TableCell></TableRow>
              ) : filtered.map(emp => (
                <TableRow key={emp.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{emp.nome}</Typography>
                    {emp.email && <Typography variant="caption" color="text.secondary">{emp.email}</Typography>}
                  </TableCell>
                  <TableCell>{emp.departamento || '—'}</TableCell>
                  <TableCell>{emp.cargo || '—'}</TableCell>
                  <TableCell align="center">
                    {emp.nfc_uid
                      ? <Chip icon={<NfcIcon />} label="Activo" size="small" color="success" />
                      : <Chip label="Sem bracelet" size="small" color="default" />}
                  </TableCell>
                  <TableCell align="right">{fmt(Number(emp.reward_amount))}</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={Number(emp.saldo_bonus) > 0 ? 700 : 400}
                      color={Number(emp.saldo_bonus) > 0 ? 'success.main' : 'text.secondary'}>
                      {Number(emp.saldo_bonus) > 0 ? fmt(Number(emp.saldo_bonus)) : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CorporatePortalEmployees;
